"use client";

import type { SessionAgentState } from "@/shared/lesson/view-contracts";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpen, Mic } from "lucide-react";
import { useEffect, useState } from "react";
import { LoadingChicky } from "./LoadingChicky";

interface EmptyStateProps {
  onViewSummary?: () => void;
  hasSummary?: boolean;
  isZenMode?: boolean;
  agentState?: SessionAgentState | string;
  interactiveChicky?: boolean;
  fillAvailableHeight?: boolean;
}

const phrases = [
  "Checking your level and recent practice...",
  "Preparing useful correction cues...",
  "Choosing a strong first prompt...",
  "Finding the sentence that will make you think twice...",
];

const zenPhrases = [
  "Listening to the silence...",
  "Observing the present moment...",
  "Connecting with your breath...",
  "Creating space for reflection...",
  "Preparing to listen deeply...",
];

export function EmptyState({
  onViewSummary,
  hasSummary,
  isZenMode = false,
  agentState = "idle",
  interactiveChicky = false,
  fillAvailableHeight = false,
}: EmptyStateProps) {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const activePhrases = isZenMode ? zenPhrases : phrases;

  useEffect(() => {
    const interval = setInterval(() => {
      setPhraseIndex((prev) => (prev + 1) % activePhrases.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [activePhrases.length]);

  return (
    <div
      className={`flex w-full flex-col items-center justify-center px-4 ${
        fillAvailableHeight ? "min-h-72 flex-1 py-8" : "min-h-[50vh] py-12"
      }`}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="mb-6"
      >
        {isZenMode ? (
          <div className="w-32 h-32 rounded-full border border-white/10 flex items-center justify-center bg-white/5 animate-pulse-slow">
            <div className="w-16 h-16 rounded-full bg-white/10 blur-xl" />
          </div>
        ) : (
          <LoadingChicky agentState={agentState} interactive={interactiveChicky} />
        )}
      </motion.div>

      <motion.h3
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className={`mb-3 text-center text-2xl font-bold ${
          isZenMode ? "font-serif text-white" : "text-white"
        }`}
      >
        {isZenMode ? "Begin your reflection" : "Preparing your session"}
      </motion.h3>

      <div className="relative mb-3 h-8 w-full max-w-md">
        <AnimatePresence mode="wait">
          <motion.p
            key={phraseIndex}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className={`absolute inset-0 text-center text-sm font-medium ${isZenMode ? "font-serif text-white/60" : "text-orange-300"}`}
          >
            {activePhrases[phraseIndex]}
          </motion.p>
        </AnimatePresence>
      </div>

      <motion.p
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="mx-auto mb-7 max-w-md text-center text-[15px] leading-7 text-white/50"
      >
        {isZenMode
          ? "Speak naturally and let your thoughts flow layer by layer."
          : "Start speaking when ready. Chicky will listen, correct, and turn the session into useful review."}
      </motion.p>

      {hasSummary && onViewSummary && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mb-7"
        >
          <motion.button
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
            onClick={onViewSummary}
            className="mx-auto flex h-10 items-center justify-center gap-2 rounded-md border border-white/10 bg-white/[0.04] px-4 text-sm font-semibold text-white/65 transition-colors hover:border-orange-300/30 hover:bg-white/[0.07] hover:text-white"
          >
            <BookOpen size={16} />
            <span>Last session summary</span>
          </motion.button>
        </motion.div>
      )}

      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="flex items-center justify-center gap-2 text-sm text-white/40"
      >
        {isZenMode ? (
          <div className="w-2 h-2 rounded-full bg-white/40 animate-pulse" />
        ) : (
          <Mic size={16} className="text-orange-300" />
        )}
        <span>{isZenMode ? "Listening..." : "Waiting for Chicky to begin..."}</span>
      </motion.div>
    </div>
  );
}
